import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, startWith, map } from 'rxjs';
import { DialogBase, LocationInfo, RentalObjectInfo } from 't4core';


@Component({
  selector: 'TireChangeDialog',
  templateUrl: 'TireChangeDialog.html',
  styleUrls: ['./TireChangeDialog.css'],
})
export class TireChangeDialog extends DialogBase implements OnInit {

  // Credit side
  locationControl = new FormControl<LocationInfo | string>('');
  creditAmountControl = new FormControl<number | null>(400);

  // Debit side
  debitLocationControl = new FormControl<LocationInfo | string>('');
  debitAmountControl = new FormControl<number | null>(800);

  //Trailer
  trailerControl = new FormControl<RentalObjectInfo | string>('');
  filteredTrailers!: Observable<RentalObjectInfo[]>;


  public locations: LocationInfo[];
  public trailers: RentalObjectInfo[];

  public debitLocationEnabled: boolean = false;
  public loading: boolean = false;

  // < ===== AutoComplete ===== >
  creditFilteredLocations!: Observable<LocationInfo[]>;
  debitFilteredLocations!: Observable<LocationInfo[]>;

    constructor(
      public dialogRef: MatDialogRef<TireChangeDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
      super("TireChangeDialog");
      }

  public async ngOnInit() {
    this.loading = true;

    if (this.data != null)
      this.locationControl.setValue(this.data);

    await this.loadLocations();
    await this.loadTrailers();
    this.initFilter();
    this.initSubsciption();

    this.loading = false;
  }


  public async loadLocations() {
    this.locations = await this.Api.get<LocationInfo[]>("OneWay/LoadLocations");
  }

  public async loadTrailers() {
    this.trailers = await this.Api.get<RentalObjectInfo[]>("RentalObject/GetUndoneTireChanges");
  }


  public async registerTransaction() {
    var creditAmount = this.creditAmountControl.value;
    var debitAmount = this.debitAmountControl.value;

    var credValue = this.locationControl.value;
    var debValue = this.debitLocationControl.value;

    var trailerValue = this.trailerControl.value;

    // Validate credited location
    var hasCredit = typeof credValue === 'object' && credValue;
    var credLocation: LocationInfo = hasCredit ? (credValue as LocationInfo) : null;

    // Validate credit amount
    if (hasCredit && (creditAmount == null || isNaN(creditAmount))) return;

    //Validate trailer selected
    if (typeof trailerValue !== 'object' || !trailerValue) return;
    var trailer: RentalObjectInfo = trailerValue;
    let confirmed = false;

    var debLocation: LocationInfo;
    var hasDebit = typeof debValue === 'object' && debValue;

    if (!hasDebit) {
      if (!hasCredit) return;
      confirmed = await this.UI.confirm(
        "Turbo.OneWay.TireChangeDialog.headConfirmCreditRegister",
        "Are you sure",
        "Turbo.OneWay.TireChangeDialog.msgConfirmCreditRegister_1",
        "Are you sure you want register a tire change to {2}, and add a credit of {0}kr to {1}? ",
        null,
        [creditAmount, credLocation.Name, trailer.Title]
      );
    } else {
      // Validate debited location
      if (typeof debValue !== 'object' || !debValue) return;
      debLocation = debValue;

      // Validate debit amount
      if (debitAmount == null || isNaN(debitAmount)) return;

      if (hasCredit) {
        confirmed = await this.UI.confirm(
          "Turbo.OneWay.TireChangeDialog.headConfirmDebitRegister",
          "Are you sure",
          "Turbo.OneWay.TireChangeDialog.msgConfirmDebitRegister",
          "Are you sure you want to register a tire change to {4}, add a credit of {0}kr to {1}, and a debit of {2}kr to {3}?",
          null,
          [creditAmount, credLocation.Name, debitAmount, debLocation.Name, trailer.Title]
        );
      } else {
        confirmed = await this.UI.confirm(
          "Turbo.OneWay.TireChangeDialog.headConfirmDebitRegister",
          "Are you sure",
          "Turbo.OneWay.TireChangeDialog.msgConfirmOnlyDebitRegister",
          "Are you sure you want to register a tire change to {2}, and add a debit of {0}kr to {1}?",
          null,
          [debitAmount, debLocation.Name, trailer.Title]
        );
      }
    }

    if (!confirmed) return;

    creditAmount = hasCredit ? -Math.abs(creditAmount) : 0;

    var requestObj: any = {
      obj: trailer.Id,
      loc: hasCredit ? credLocation.Id : null,
      amount: creditAmount
    };

    if (hasDebit) {
      requestObj.debitLoc = debLocation.Id;
      requestObj.debitAmount = debitAmount;
    }

    var registerResult = await this.Api.post<boolean>("RentalObject/RegisterTireChange", null, requestObj);

    if (registerResult) {
      this.UI.snackBar("Tire registration saved!");
      this.dialogRef.close(true);
    }
  }

  get isConfirmDisabled(): boolean {
    const selectedTrailer = this.trailerControl.value;
    if (typeof selectedTrailer !== 'object' || !selectedTrailer || selectedTrailer.Id == undefined) return true;

    // Credit validation
    const creditLoc = this.locationControl.value;
    const creditAmount = this.creditAmountControl.value;
    const isCreditValid = typeof creditLoc === 'object' && creditLoc && creditLoc.Id != undefined && creditAmount != null && !isNaN(creditAmount);

    // Debit validation
    const debitLoc = this.debitLocationControl.value;
    const debitAmount = this.debitAmountControl.value;
    const isDebitValid = typeof debitLoc === 'object' && debitLoc && debitLoc.Id != undefined && debitAmount != null && !isNaN(debitAmount);

    if (!isCreditValid && !isDebitValid) return true;

    return false;
  }

  toggleDebit(enabled: boolean) {
    if (enabled) {
      this.debitLocationControl.enable();
      this.debitAmountControl.enable();
      this.debitLocationEnabled = true;
    } else {
      this.debitLocationControl.disable();
      this.debitAmountControl.disable();
      this.debitLocationEnabled = false;
    }
  }

  initSubsciption() {
    this.trailerControl.valueChanges.subscribe(value => {
      if (typeof value === 'object' && value?.Id) {

        const locationCheck = this.locations.find(loc => loc.Id === value.RentalLocation.Id);
        if (locationCheck) {
          this.locationControl.setValue(locationCheck);
        }
        //If it should only update the first time?
        //const creditLoc = this.locationControl.value;
        //// If credit location is not yet selected
        //if (typeof creditLoc !== 'object' || !creditLoc?.Id) {
        //  const locationCheck = this.locations.find(loc => loc.Id === value.RentalLocation.Id);
        //  if (locationCheck) {
        //    this.locationControl.setValue(locationCheck);
        //  }
        //}
      }
    });

  }


  // < ===== AutoComplete ===== >

  public initFilter() {
    this.creditFilteredLocations = this.locationControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (typeof value === 'string') {
          return value;
        } else {
          return (value as LocationInfo)?.Name ?? '';
        }
      }),
      map((name: string) => this._filter(name)) 
    );

    this.debitFilteredLocations = this.debitLocationControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (typeof value === 'string') {
          return value;
        } else {
          return (value as LocationInfo)?.Name ?? '';
        }
      }),
      map((name: string) => this._filter(name))
    );


    this.filteredTrailers = this.trailerControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.Title ?? ''),
      map(name => this._filterTrailers(name))
    );

  }

  private _filter(name: string): LocationInfo[] {
    const filterValue = name.toLowerCase();
    return this.locations.filter(location =>
      location.Name.toLowerCase().includes(filterValue) ||
      location.LocationCode.toLowerCase().includes(filterValue)
    );
  }


  private _filterTrailers(query: string): RentalObjectInfo[] {
    const filterValue = query.toLowerCase();
    return this.trailers.filter(trailer =>
      trailer.Alias.toLowerCase().includes(filterValue) ||
      trailer.Title.toLowerCase().includes(filterValue)
    );
  }

  displayFn(location: LocationInfo): string {
    return location && location.Name ? `${location.LocationCode} -  ${location.Name}` : '';
  }

  displayTrailer(trailer: RentalObjectInfo): string {
    return trailer && trailer.Title ? `${trailer.Alias} – ${trailer.Title}` : '';
  }

 clearLocationSelection(type: 'credit' | 'debit') {
  if (type === 'credit') {
    this.locationControl.setValue('');
  } else if (type === 'debit') {
    this.debitLocationControl.setValue('');
  }
}


}
